import { Component, OnInit } from "@angular/core";
import { HttpEventType } from "@angular/common/http";
import Swal from "sweetalert2";
import { Administracion } from "../../../shared/model/administracion.model";
import { AdministracionService } from "../../../shared/services/administracion.services";
import { DigitalesPesv, DigitalesPesvService } from "../../../shared/services/digitales-pesv.service";
import { VersionesDocumentoService } from "../../../shared/services/versiones-documento.service";

interface ArchivoPesv {
  id: number;
  nombre: string;
  prefijo: string;
  file: File | null;
  estado: "" | "subiendo" | "exito" | "error";
  mensaje: string;
  progreso: number;
  // extensiones permitidas, por ejemplo: ['doc', 'docx', 'pdf']
  formatos?: string[];
  // etiqueta original desde BD, por ejemplo: 'word/pdf'
  formatosLabel?: string;
  // nombre del archivo guardado en almacenamiento
  nombreGuardado?: string;
}

@Component({
  selector: "app-digitales-pesv",
  templateUrl: "./digitales-pesv.component.html",
  styleUrls: ["./digitales-pesv.component.scss"],
})
export class DigitalesPesvComponent implements OnInit {
  // Configuración dinámica desde backend (digitales_pesv)
  archivos: ArchivoPesv[] = [];

  administraciones: Administracion[] = [];
  empresaNit: string | null = null;

  constructor(
    private administracionesService: AdministracionService,
    private digitalesPesvService: DigitalesPesvService,
    private versionesDocumentoService: VersionesDocumentoService,
  ) {}

  ngOnInit(): void {
    this.cargarDataAdmin();
    this.cargarConfiguracionDigitalesPesv();
  }

  // Cargar definiciones de documentos PESV desde el backend
  private cargarConfiguracionDigitalesPesv(): void {
    this.digitalesPesvService.getAll().subscribe({
      next: (data: DigitalesPesv[]) => {
        console.debug('cargarConfiguracionDigitalesPesv - recibidos digitalesPesv', { total: data?.length, sample: (data || []).slice(0, 20) });
        // Solo activos (estado = 1)
        const activos = data.filter((d) => d.estado === 1);
        this.archivos = activos.map((item) => ({
          id: item.id,
          nombre: item.tipo_documentos,
          prefijo: item.siglas,
          file: null,
          estado: "",
          mensaje: "",
          progreso: 0,
          formatos: this.obtenerExtensionesDesdeFormatos(item.formatos),
          formatosLabel: item.formatos || "",
        }));
      },
      error: (err) => {
        console.error("Error cargando configuración de digitales PESV:", err);
      },
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.add("drag-over");
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove("drag-over");
  }

  // ==== Manejo de archivos para cards dinámicos de digitales PESV ====

  onFileChangeCard(event: any, archivo: ArchivoPesv): void {
    const file: File = event.target.files[0];
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const formatosPermitidos = archivo.formatos;

    if (formatosPermitidos && formatosPermitidos.length > 0) {
      if (!formatosPermitidos.includes(extension)) {
        const descripcion = archivo.formatosLabel ||
          formatosPermitidos.map((f) => `.${f}`).join(", ");
        Swal.fire(
          "Formato inválido",
          `Solo se aceptan: ${descripcion}`,
          "warning",
        );
        event.target.value = "";
        return;
      }
    }

    archivo.file = file;
    archivo.estado = "";
    archivo.mensaje = "";
    archivo.progreso = 0;
    event.target.value = "";
  }

  onDropCard(event: DragEvent, archivo: ArchivoPesv): void {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove("drag-over");

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      const formatosPermitidos = archivo.formatos;

      if (formatosPermitidos && formatosPermitidos.length > 0) {
        if (!formatosPermitidos.includes(extension)) {
          const descripcion = archivo.formatosLabel ||
            formatosPermitidos.map((f) => `.${f}`).join(", ");
          Swal.fire(
            "Formato inválido",
            `Solo se aceptan: ${descripcion}`,
            "warning",
          );
          event.dataTransfer.clearData();
          return;
        }
      }

      archivo.file = file;
      archivo.estado = "";
      archivo.mensaje = "";
      archivo.progreso = 0;
      event.dataTransfer.clearData();
    }
  }

  removeFileCard(archivo: ArchivoPesv): void {
    archivo.file = null;
    archivo.estado = "";
    archivo.mensaje = "";
    archivo.progreso = 0;
  }

  private normalizarSegmento(valor: string): string {
    return (valor || "")
      .toString()
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  }

  private extensionDesdeMime(mimeType: string): string {
    if (!mimeType) return "bin";
    if (mimeType.includes("pdf")) return "pdf";
    if (mimeType.includes("wordprocessingml") || mimeType.includes("msword")) return "docx";
    if (mimeType.includes("spreadsheetml") || mimeType.includes("excel")) return "xlsx";
    if (mimeType.includes("presentationml") || mimeType.includes("powerpoint")) return "pptx";
    if (mimeType.includes("image/")) return mimeType.split("/")[1] || "img";
    return "bin";
  }

  private extensionDesdeConfiguracion(archivo: ArchivoPesv): string {
    const formatos = archivo.formatos || [];
    // Respetar el orden de configuración del documento para priorizar el formato esperado
    for (const formato of formatos) {
      if (formato === "doc" || formato === "docx") return "docx";
      if (formato === "pdf") return "pdf";
      if (formato === "xls" || formato === "xlsx") return "xlsx";
    }
    return "bin";
  }

  private async extensionDesdeFirma(blob: Blob, archivo: ArchivoPesv): Promise<string> {
    try {
      const buffer = await blob.slice(0, 8).arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Firma PDF: 25 50 44 46 => %PDF
      if (
        bytes.length >= 4 &&
        bytes[0] === 0x25 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x44 &&
        bytes[3] === 0x46
      ) {
        return "pdf";
      }

      // Office Open XML (docx/xlsx/pptx) suele venir en ZIP: 50 4B => PK
      if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
        const formatos = archivo.formatos || [];
        if (formatos.includes("doc") || formatos.includes("docx")) return "docx";
        if (formatos.includes("xls") || formatos.includes("xlsx")) return "xlsx";
        return "docx";
      }
    } catch (e) {
      console.warn("No se pudo detectar firma binaria del archivo:", e);
    }

    return "";
  }

  private extraerExtensionDeNombre(nombre: string | undefined): string {
    if (!nombre) return "";
    const limpia = nombre.replace(/\"/g, "").trim();
    const partes = limpia.split(".");
    if (partes.length < 2) return "";
    return (partes.pop() || "").toLowerCase();
  }

  private extraerNombreDeContentDisposition(contentDisposition: string | null): string {
    if (!contentDisposition) return "";
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8?.[1]) {
      return decodeURIComponent(utf8[1]);
    }
    const normal = /filename=\"?([^\";]+)\"?/i.exec(contentDisposition);
    return normal?.[1] || "";
  }

  private extraerListaDocumentos(respuesta: any): any[] {
    if (Array.isArray(respuesta?.documentos)) {
      return respuesta.documentos;
    }
    if (Array.isArray(respuesta?.archivos)) {
      return respuesta.archivos;
    }
    if (Array.isArray(respuesta)) {
      return respuesta;
    }
    return [];
  }

  private construirRutaCompletaArchivo(carpeta: string, documento: any): string {
    const rutaDirecta =
      documento?.rutaCompleta ||
      documento?.ruta_completa ||
      documento?.path ||
      documento?.ruta;

    if (typeof rutaDirecta === "string" && rutaDirecta.trim() !== "") {
      return rutaDirecta;
    }

    const nombreArchivo =
      documento?.name ||
      documento?.nombre ||
      documento?.archivo ||
      documento?.fileName ||
      documento?.filename;

    if (typeof nombreArchivo === "string" && nombreArchivo.trim() !== "") {
      return `${carpeta}/${nombreArchivo}`;
    }

    return "";
  }

  descargarArchivo(archivo: ArchivoPesv): void {
    Swal.fire({
      title: "Descargando...",
      text: "Por favor espera mientras se descarga el documento vigente.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      didOpen: () => {
        Swal.showLoading();
      },
    });

    if (!this.empresaNit) {
      Swal.close();
      Swal.fire("Error", "No se pudo obtener el NIT de la empresa.", "error");
      return;
    }

    this.versionesDocumentoService.downloadDigitalPesv(this.empresaNit, archivo.prefijo).subscribe({
      next: async (response: any) => {
        await this.descargarBlobDesdeRespuesta(response, archivo, "Documento descargado correctamente");
      },
      error: (err) => {
        Swal.close();
        console.error("Error al descargar archivo digital PESV:", err);
        Swal.fire("Error", "No se pudo descargar el documento", "error");
      },
    });
  }

  async subirArchivo(archivo: ArchivoPesv) {
    if (!this.empresaNit) {
      Swal.fire("Error", "No se pudo obtener el NIT de la empresa.", "error");
      return;
    }

    if (!archivo.file) {
      Swal.fire("Sin archivo", "Debes seleccionar un archivo para subir.", "warning");
      return;
    }
    try {
      archivo.estado = "subiendo";
      archivo.progreso = 0;

      this.versionesDocumentoService
        .uploadDigitalPesv(archivo.file as File, this.empresaNit, archivo.prefijo)
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              archivo.progreso = Math.round((100 * event.loaded) / event.total);
            } else if (event.type === HttpEventType.Response) {
              archivo.estado = "exito";
              archivo.mensaje = "Subido exitosamente";
            }
          },
          error: (error) => {
            archivo.estado = "error";
            archivo.mensaje = "Error al subir";
            console.error("Error al subir archivo digital PESV:", error);
            Swal.fire("Error", "No se pudo guardar el documento digital PESV.", "error");
          },
        });
    } catch (error) {
      archivo.estado = "error";
      archivo.mensaje = "Error al subir";
      console.error("Error al subir archivo digital PESV:", error);
      Swal.fire("Error", "No se pudo guardar el documento digital PESV.", "error");
    }
  }

  subirTodos() {
    let contador = 0;

    // Subir los dinámicos de digitales_pesv
    this.archivos.forEach((a) => {
      if (a.file && a.estado !== "exito" && a.estado !== "subiendo") {
        this.subirArchivo(a);
        contador++;
      }
    });

    if (contador === 0) {
      Swal.fire("Sin archivos", "No hay archivos pendientes para subir.", "info");
    }
  }

  limpiarCompletados() {
    this.archivos = this.archivos.map((a) =>
      a.estado === "exito"
        ? { ...a, file: null, estado: "", mensaje: "", progreso: 0 }
        : a,
    );
  }

  limpiarTodo() {
    this.archivos = this.archivos.map((a) => ({
      ...a,
      file: null,
      estado: "",
      mensaje: "",
      progreso: 0,
    }));
  }

  private cargarDataAdmin(): void {
    this.administracionesService.obtenerTodas().subscribe({
      next: (data) => {
        this.administraciones = data;
        this.empresaNit = this.administraciones[0]?.nit ? String(this.administraciones[0].nit) : null;
      },
      error: (err) => {
        console.error("Error al cargar las administraciones:", err);
      },
    });
  }



  tieneArchivos(): boolean {
    return (
      this.archivos.some((a) => a.file !== null)
    );
  }

  private async descargarBlobDesdeRespuesta(
    respuesta: any,
    archivo: ArchivoPesv,
    mensajeExito: string,
  ): Promise<void> {
    Swal.close();

    const blob: Blob = respuesta?.body || respuesta;
    const contentDisposition = respuesta?.headers?.get("content-disposition") || null;
    const nombreHeader = this.extraerNombreDeContentDisposition(contentDisposition);

    const extensionHeader = this.extraerExtensionDeNombre(nombreHeader);
    const extensionGuardada = this.extraerExtensionDeNombre(archivo.nombreGuardado);
    const extensionMime = this.extensionDesdeMime(blob.type || "");
    const extensionFirma = await this.extensionDesdeFirma(blob, archivo);
    const extensionConfig = this.extensionDesdeConfiguracion(archivo);
    const extension =
      extensionHeader ||
      extensionGuardada ||
      (extensionMime !== "bin" ? extensionMime : "") ||
      extensionFirma ||
      extensionConfig ||
      "bin";

    const nombreBase = this.normalizarSegmento(archivo.nombre).toUpperCase() || "DOCUMENTO";
    const nombreDescarga = `${nombreBase}.${extension}`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = nombreDescarga;
    link.click();
    window.URL.revokeObjectURL(url);

    Swal.fire("Éxito", mensajeExito, "success");
  }

  // Construye la lista de extensiones permitidas a partir del campo formatos
  // Ejemplos de item.formatos: 'word/pdf', 'word', 'pdf', 'excel', 'pdf,word'
  private obtenerExtensionesDesdeFormatos(formatos: string | null | undefined): string[] {
    if (!formatos) {
      return [];
    }

    const tokens = formatos
      .toLowerCase()
      .split(/[,/\s]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const extensiones: string[] = [];

    tokens.forEach((token) => {
      switch (token) {
        case "word":
          extensiones.push("doc", "docx");
          break;
        case "pdf":
          extensiones.push("pdf");
          break;
        case "excel":
          extensiones.push("xls", "xlsx");
          break;
        default:
          // Si ya viene como extensión (ej: 'doc', 'docx'), la usamos tal cual
          extensiones.push(token.replace(".", ""));
          break;
      }
    });

    // Eliminar duplicados
    return Array.from(new Set(extensiones));
  }

  // Para usar en el atributo [attr.accept] del input file
  getAccept(archivo: ArchivoPesv): string | null {
    if (!archivo.formatos || archivo.formatos.length === 0) {
      return null;
    }
    return archivo.formatos.map((f) => `.${f}`).join(",");
  }
}
